import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import {
  EVENT_TYPE_CHANNELS,
  STATIC_VIDEO_CHANNEL_LABELS,
  STATIC_VIDEO_CHANNELS,
  isStaticVideoChannel,
  type StaticVideoChannel,
} from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  Select,
  Skeleton,
  TextField,
  Textarea,
  Toggle,
  useToast,
} from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { usePermissions, useWorkspace } from '../../lib/session';
import { useUserDirectory } from '../users/directory';
import { absoluteBookingUrl, bookingLinkText, canCreateEventTypes, copyText } from './EventTypesPage';
import {
  useDeleteEventType,
  useEventType,
  useSaveEventType,
  type EventType,
  type EventTypeChannel,
  type EventTypeInput,
} from './api';
import fields from './fields.module.css';
import styles from './EventTypeEditorPage.module.css';

/**
 * The „Ort" options; the video room names the recording only while the option „Aufzeichnung und Transkript"
 * is on (D-163). Google Meet, Microsoft Teams and Zoom use the advisor's personal link from the profile
 * (docs/dev/api-appointments.md „Static meeting links").
 */
function channelOptions(recording: boolean): ReadonlyArray<{ value: EventTypeChannel; label: string }> {
  return [
    {
      value: 'video',
      label: recording ? 'DAMOKLES Meetingraum · Aufzeichnung und Transkript' : 'DAMOKLES Meetingraum',
    },
    ...STATIC_VIDEO_CHANNELS.map((value) => ({ value, label: STATIC_VIDEO_CHANNEL_LABELS[value] })),
    { value: 'phone', label: 'Telefon' },
    { value: 'on_site', label: 'Vor Ort' },
    { value: 'property_address', label: 'Objektadresse' },
  ];
}

const VIDEO_HINT =
  'Der Beitrittslink wird pro Termin erzeugt und steht in Bestätigung, Erinnerungen und im Mandantenportal.';

/** „Google-Meet-Link" / „Microsoft-Teams-Link" / „Zoom-Link" — the compound of the API's error messages. */
function linkNoun(channel: StaticVideoChannel): string {
  return `${STATIC_VIDEO_CHANNEL_LABELS[channel].replaceAll(' ', '-')}-Link`;
}

function staticLinkHint(channel: StaticVideoChannel): string {
  return `Jeder Berater nutzt seinen persönlichen ${linkNoun(channel)} aus dem Profil. Er steht in Bestätigung, Erinnerungen und im Mandantenportal.`;
}

/** „kein Google-Meet-Link im Profil" — the marker of a user without a link for the chosen static channel. */
function missingLinkNote(channel: StaticVideoChannel): string {
  return `kein ${linkNoun(channel)} im Profil`;
}

/** Only phone and the two on-site channels take a free-text „Ortsangabe". */
function takesLocationText(channel: EventTypeChannel): boolean {
  return channel !== 'video' && !isStaticVideoChannel(channel);
}

function intField(min: number, max: number) {
  return z
    .string()
    .trim()
    .regex(/^\d+$/, 'Bitte eine ganze Zahl eingeben.')
    .refine((value) => Number(value) >= min && Number(value) <= max, `Zwischen ${min} und ${max}.`);
}

const formSchema = z.object({
  name: z.string().trim().min(1, 'Pflichtangabe.').max(120, 'Höchstens 120 Zeichen.'),
  slug: z
    .string()
    .trim()
    .max(63, 'Höchstens 63 Zeichen.')
    .regex(/^$|^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche.'),
  durationMinutes: intField(5, 480),
  description: z.string().max(2000, 'Höchstens 2000 Zeichen.'),
  channel: z.enum(EVENT_TYPE_CHANNELS),
  locationText: z.string().trim().max(200, 'Höchstens 200 Zeichen.'),
  assignment: z.enum(['single', 'round_robin']),
  ownerId: z.string(),
  members: z.array(
    z.object({ userId: z.string(), name: z.string(), existing: z.boolean(), checked: z.boolean() }),
  ),
  bufferBeforeMinutes: intField(0, 240),
  bufferAfterMinutes: intField(0, 240),
  slotIntervalMinutes: intField(5, 120),
  minLeadTimeHours: intField(0, 720),
  bookableUntilDays: intField(1, 365),
  createsDeal: z.boolean(),
  recordingEnabled: z.boolean(),
  addressFromProperty: z.boolean(),
  internalOnly: z.boolean(),
  active: z.boolean(),
  crmContactTimeline: z.boolean(),
  crmDealTimeline: z.boolean(),
  crmPropertyTimeline: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface Candidate {
  userId: string;
  name: string;
}

/**
 * Member candidates: the type's members, then every other active user of the directory (`GET /users`, M8)
 * unchecked — for a new type the current user starts checked.
 */
function memberCandidates(
  type: EventType | null,
  me: Candidate,
  directory: readonly Candidate[],
): FormValues['members'] {
  const list: FormValues['members'] = (type?.members ?? []).map((member) => ({
    userId: member.userId,
    name: member.name,
    existing: true,
    checked: member.active,
  }));
  if (!list.some((entry) => entry.userId === me.userId)) {
    list.push({ userId: me.userId, name: me.name, existing: false, checked: type === null });
  }
  for (const user of directory) {
    if (!list.some((entry) => entry.userId === user.userId)) {
      list.push({ userId: user.userId, name: user.name, existing: false, checked: false });
    }
  }
  return list;
}

function defaultsFor(type: EventType | null, me: Candidate, directory: readonly Candidate[]): FormValues {
  return {
    name: type?.name ?? '',
    slug: type?.slug ?? '',
    durationMinutes: String(type?.durationMinutes ?? 30),
    description: type?.description ?? '',
    channel: type?.channel ?? 'video',
    locationText: type?.locationText ?? '',
    assignment: type?.assignment ?? 'single',
    ownerId: type?.owner?.id ?? me.userId,
    members: memberCandidates(type, me, directory),
    bufferBeforeMinutes: String(type?.bufferBeforeMinutes ?? 0),
    bufferAfterMinutes: String(type?.bufferAfterMinutes ?? 0),
    slotIntervalMinutes: String(type?.slotIntervalMinutes ?? 15),
    minLeadTimeHours: String(type?.minLeadTimeHours ?? 24),
    bookableUntilDays: String(type?.bookableUntilDays ?? 28),
    createsDeal: type?.createsDeal ?? false,
    recordingEnabled: type?.recordingEnabled ?? false,
    addressFromProperty: type?.addressFromProperty ?? false,
    internalOnly: type?.internalOnly ?? false,
    active: type?.active ?? true,
    crmContactTimeline: type?.crmContactTimeline ?? true,
    crmDealTimeline: type?.crmDealTimeline ?? true,
    crmPropertyTimeline: type?.crmPropertyTimeline ?? false,
  };
}

function toInput(values: FormValues): Partial<EventTypeInput> {
  const input: Partial<EventTypeInput> = {
    name: values.name.trim(),
    durationMinutes: Number(values.durationMinutes),
    description: values.description.trim() === '' ? null : values.description.trim(),
    channel: values.channel,
    locationText:
      !takesLocationText(values.channel) || values.locationText.trim() === ''
        ? null
        : values.locationText.trim(),
    assignment: values.assignment,
    bufferBeforeMinutes: Number(values.bufferBeforeMinutes),
    bufferAfterMinutes: Number(values.bufferAfterMinutes),
    slotIntervalMinutes: Number(values.slotIntervalMinutes),
    minLeadTimeHours: Number(values.minLeadTimeHours),
    bookableUntilDays: Number(values.bookableUntilDays),
    createsDeal: values.createsDeal,
    recordingEnabled: isStaticVideoChannel(values.channel) ? false : values.recordingEnabled,
    addressFromProperty: values.addressFromProperty,
    internalOnly: values.internalOnly,
    active: values.active,
    crmContactTimeline: values.crmContactTimeline,
    crmDealTimeline: values.crmDealTimeline,
    crmPropertyTimeline: values.crmPropertyTimeline,
  };
  if (values.slug.trim() !== '') input.slug = values.slug.trim();
  if (values.assignment === 'single') {
    input.ownerId = values.ownerId;
  } else {
    input.members = values.members
      .filter((member) => member.existing || member.checked)
      .map((member) => ({ userId: member.userId, active: member.checked }));
  }
  return input;
}

function EditorSkeleton() {
  return (
    <div className={styles.page} aria-busy="true">
      <Skeleton width={110} />
      <div className={styles.head}>
        <div>
          <Skeleton width={180} height={24} />
          <Skeleton width={220} style={{ marginTop: 8 }} />
        </div>
      </div>
      <div className={styles.grid}>
        <Skeleton variant="block" height={520} />
        <div className={styles.column}>
          <Skeleton variant="block" height={210} />
          <Skeleton variant="block" height={180} />
        </div>
      </div>
    </div>
  );
}

interface EditorProps {
  type: EventType | null;
}

function Editor({ type }: EditorProps) {
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const { toast } = useToast();
  const me = useMemo<Candidate>(
    () => ({
      userId: workspace.user.id,
      name: `${workspace.user.firstName} ${workspace.user.lastName}`.trim(),
    }),
    [workspace.user.firstName, workspace.user.id, workspace.user.lastName],
  );
  const users = useUserDirectory();
  const directory = useMemo<Candidate[]>(
    () => (users.data ?? []).map((user) => ({ userId: user.id, name: user.name })),
    [users.data],
  );
  const canEdit = type ? type.canEdit : true;
  const save = useSaveEventType(type?.id);
  const remove = useDeleteEventType();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultsFor(type, me, directory),
    disabled: !canEdit,
  });
  const { register, control, handleSubmit, watch, setError, setValue, reset, formState } = form;

  // A new state of the type (or of the user) replaces the form; the directory arriving later or refetched by
  // `user.updated` only fills the untouched fields, so it never wipes what the user has typed.
  const formSource = useRef<{ type: EventType | null; me: Candidate } | null>(null);
  useEffect(() => {
    const sourceChanged = formSource.current?.type !== type || formSource.current.me !== me;
    formSource.current = { type, me };
    reset(defaultsFor(type, me, directory), sourceChanged ? undefined : { keepDirtyValues: true });
  }, [type, me, directory, reset]);

  const channel = watch('channel');
  const recordingEnabled = watch('recordingEnabled');
  const staticChannel = isStaticVideoChannel(channel) ? channel : null;
  // Recording and transcript exist only in the DAMOKLES meeting room: a static channel switches the option off.
  useEffect(() => {
    if (staticChannel && recordingEnabled) setValue('recordingEnabled', false, { shouldDirty: true });
  }, [staticChannel, recordingEnabled, setValue]);
  const videoLinks = useMemo(
    () => new Map((users.data ?? []).map((user) => [user.id, user.videoLinks] as const)),
    [users.data],
  );
  /** The muted marker of a user without a link for the chosen static channel, if any. */
  const missingLink = (userId: string): string | undefined =>
    staticChannel && videoLinks.has(userId) && !videoLinks.get(userId)?.includes(staticChannel)
      ? missingLinkNote(staticChannel)
      : undefined;
  const assignment = watch('assignment');
  const members = watch('members');
  const name = watch('name');
  const slug = watch('slug');
  const active = watch('active');
  const serverFields = save.isError && save.error instanceof ApiError ? save.error.fields : {};
  const bookingUrl = type?.bookingUrl ?? null;
  const previewLink = bookingUrl ? bookingLinkText(bookingUrl) : null;
  const ownerId = watch('ownerId');
  const ownerOptions = useMemo(() => {
    const options: Candidate[] = [];
    if (type?.owner) options.push({ userId: type.owner.id, name: type.owner.name });
    if (!options.some((option) => option.userId === me.userId)) options.push(me);
    for (const user of directory) {
      if (!options.some((option) => option.userId === user.userId)) options.push(user);
    }
    return options;
  }, [type, me, directory]);

  const submit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync(toInput(values));
      toast({ message: type ? 'Ereignistyp gespeichert.' : 'Ereignistyp angelegt.' });
      if (!type) await navigate(`/calendar/event-types/${saved.id}`, { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fields)) {
          if (field in values) setError(field as keyof FormValues, { message });
        }
      }
    }
  });

  async function copy() {
    if (!bookingUrl) return;
    const ok = await copyText(absoluteBookingUrl(bookingUrl));
    toast({ message: ok ? 'Link kopiert.' : 'Der Link konnte nicht kopiert werden.' });
  }

  async function confirmDelete() {
    if (!type) return;
    try {
      await remove.mutateAsync(type.id);
      toast({ message: 'Ereignistyp gelöscht.' });
      await navigate('/calendar/event-types', { replace: true });
    } catch {
      setDeleteOpen(false);
    }
  }

  const fieldErrors = formState.errors;
  const message = (key: keyof FormValues): string | undefined =>
    fieldErrors[key]?.message ?? serverFields[key];
  const generalError =
    save.isError && save.error instanceof ApiError && Object.keys(save.error.fields).length === 0
      ? save.error.message
      : save.isError && !(save.error instanceof ApiError)
        ? errorMessage(save.error)
        : null;

  return (
    <form className={styles.page} onSubmit={(event) => void submit(event)} noValidate>
      <Link to="/calendar/event-types" className={styles.crumb}>
        ← Ereignistypen
      </Link>
      <div className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>{name.trim() || (type ? type.name : 'Neuer Ereignistyp')}</h1>
          <div className={styles.headLink}>
            {previewLink ??
              (type
                ? 'Kein Link'
                : `${window.location.host}/book/${workspace.organizationSlug}/${slug.trim() || '…'}`)}
          </div>
        </div>
        <span className={styles.headActions}>
          {type ? (
            <Link to={`/calendar/event-types/${type.id}/reminders`} className={styles.chainLink}>
              Ablauf{type.hasCustomReminderChain ? ' · Eigener Ablauf' : ' · Org-Standard'}
            </Link>
          ) : null}
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <span className={styles.activeToggle}>
                <span className={clsx(styles.activeLabel, !active && styles.activeLabelOff)}>Aktiv</span>
                <Toggle
                  label="Aktiv"
                  labelHidden
                  size="large"
                  checked={field.value}
                  onChange={field.onChange}
                  disabled={!canEdit}
                  className={styles.toggle38}
                />
              </span>
            )}
          />
          {canEdit ? (
            <Button type="submit" size="small" className={styles.saveButton} loading={save.isPending}>
              Speichern
            </Button>
          ) : null}
        </span>
      </div>

      {!canEdit ? (
        <Banner tone="neutral">Diesen Ereignistyp bearbeiten Admins oder der Besitzer.</Banner>
      ) : null}
      {generalError ? <Banner tone="danger">{generalError}</Banner> : null}
      {remove.isError ? <Banner tone="danger">{errorMessage(remove.error)}</Banner> : null}

      <div className={styles.grid}>
        <section className={styles.card} aria-labelledby="event-type-basics">
          <h2 id="event-type-basics" className={styles.cardTitle}>
            Grunddaten
          </h2>
          <div className={styles.stack}>
            <TextField
              label="Name"
              className={fields.field}
              error={message('name')}
              autoFocus={!type}
              {...register('name')}
            />
            <div className={styles.slugRow}>
              <TextField
                label="Link-Slug"
                className={fields.field}
                placeholder={type ? undefined : 'wird aus dem Namen erzeugt'}
                error={message('slug')}
                {...register('slug')}
              />
              <TextField
                label="Dauer"
                className={fields.field}
                inputMode="numeric"
                trailing={<span className={styles.unit}>Min</span>}
                error={message('durationMinutes')}
                {...register('durationMinutes')}
              />
            </div>
            <Textarea
              label="Beschreibung für den Gast"
              className={clsx(fields.field, styles.description)}
              rows={2}
              error={message('description')}
              {...register('description')}
            />
            <div>
              <Select
                label="Ort"
                className={fields.field}
                options={channelOptions(recordingEnabled)}
                error={message('channel')}
                {...register('channel')}
              />
              {channel === 'video' ? <div className={fields.hint}>{VIDEO_HINT}</div> : null}
              {staticChannel ? <div className={fields.hint}>{staticLinkHint(staticChannel)}</div> : null}
            </div>
            {takesLocationText(channel) ? (
              <TextField
                label="Ortsangabe"
                className={fields.field}
                optional
                hint="Erscheint in Bestätigung und Erinnerungen."
                error={message('locationText')}
                {...register('locationText')}
              />
            ) : null}
            <div className={styles.assignmentRow}>
              <Select
                label="Vergabe"
                className={fields.field}
                options={[
                  { value: 'single', label: 'Einzel' },
                  { value: 'round_robin', label: 'Round-Robin' },
                ]}
                {...register('assignment')}
              />
              {assignment === 'round_robin' ? (
                <fieldset className={styles.members}>
                  <legend className={fields.label}>Mitglieder</legend>
                  <div className={styles.memberList}>
                    {members.map((member, index) => (
                      <Checkbox
                        key={member.userId}
                        label={member.name}
                        description={missingLink(member.userId)}
                        className={clsx(styles.member, !member.checked && styles.memberOff)}
                        {...register(`members.${index}.checked` as const)}
                      />
                    ))}
                  </div>
                  {message('members') ? <div className={styles.fieldError}>{message('members')}</div> : null}
                </fieldset>
              ) : (
                <Select
                  label="Berater"
                  className={fields.field}
                  options={ownerOptions.map((option) => {
                    const note = missingLink(option.userId);
                    return { value: option.userId, label: note ? `${option.name} · ${note}` : option.name };
                  })}
                  hint={message('ownerId') ? undefined : missingLink(ownerId)}
                  error={message('ownerId')}
                  {...register('ownerId')}
                />
              )}
            </div>
          </div>
          {type && canEdit && type.systemKey === null ? (
            <div className={styles.dangerRow}>
              <Button variant="destructive" size="small" onClick={() => setDeleteOpen(true)}>
                Ereignistyp löschen
              </Button>
            </div>
          ) : null}
        </section>

        <div className={styles.column}>
          <section className={styles.card} aria-labelledby="event-type-rules">
            <h2 id="event-type-rules" className={styles.cardTitle}>
              Buchungsregeln
            </h2>
            <div className={styles.rulesGrid}>
              <TextField
                label="Puffer davor"
                className={fields.field}
                inputMode="numeric"
                trailing={<span className={styles.unit}>Min</span>}
                error={message('bufferBeforeMinutes')}
                {...register('bufferBeforeMinutes')}
              />
              <TextField
                label="Puffer danach"
                className={fields.field}
                inputMode="numeric"
                trailing={<span className={styles.unit}>Min</span>}
                error={message('bufferAfterMinutes')}
                {...register('bufferAfterMinutes')}
              />
              <TextField
                label="Slot-Raster"
                className={fields.field}
                inputMode="numeric"
                trailing={<span className={styles.unit}>Min</span>}
                error={message('slotIntervalMinutes')}
                {...register('slotIntervalMinutes')}
              />
              <TextField
                label="Mindestvorlauf"
                className={fields.field}
                inputMode="numeric"
                trailing={<span className={styles.unit}>Std</span>}
                error={message('minLeadTimeHours')}
                {...register('minLeadTimeHours')}
              />
              <TextField
                label="Buchbar im Voraus"
                className={clsx(fields.field, styles.span2)}
                inputMode="numeric"
                trailing={<span className={styles.unit}>Tage</span>}
                error={message('bookableUntilDays')}
                {...register('bookableUntilDays')}
              />
            </div>
          </section>

          <section className={styles.card} aria-labelledby="event-type-crm">
            <h2 id="event-type-crm" className={styles.cardTitle}>
              Bezug im CRM
            </h2>
            <p className={styles.cardText}>Wohin der Termin und der Rückblick geschrieben werden.</p>
            <div className={styles.toggles}>
              {(
                [
                  ['crmContactTimeline', 'Eintrag in die Kontakt-Timeline'],
                  ['crmDealTimeline', 'Eintrag in die Deal-Timeline, wenn ein Deal verknüpft ist'],
                  ['crmPropertyTimeline', 'Eintrag in die Objekt-Timeline'],
                ] as const
              ).map(([key, label]) => (
                <Controller
                  key={key}
                  control={control}
                  name={key}
                  render={({ field }) => (
                    <Toggle
                      label={label}
                      size="large"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={!canEdit}
                      className={clsx(styles.toggle, styles.toggle38, !field.value && styles.toggleOff)}
                    />
                  )}
                />
              ))}
            </div>
          </section>

          <div className={styles.linkRow}>
            <span className={styles.linkText}>{previewLink ?? 'Kein Link'}</span>
            <Button
              variant="secondary"
              size="small"
              className={styles.linkButton}
              disabled={!bookingUrl}
              onClick={() => void copy()}
            >
              Kopieren
            </Button>
            <Button
              variant="secondary"
              size="small"
              className={styles.linkButton}
              disabled={!bookingUrl}
              onClick={() => {
                if (bookingUrl) window.open(absoluteBookingUrl(bookingUrl), '_blank', 'noopener');
              }}
            >
              Ansehen
            </Button>
          </div>

          <section className={styles.card} aria-labelledby="event-type-options">
            <h2 id="event-type-options" className={styles.cardTitle}>
              Optionen
            </h2>
            <p className={styles.cardText}>Was der Ereignistyp beim Buchen auslöst.</p>
            <div className={styles.toggles}>
              {(
                [
                  ['createsDeal', 'Legt beim Buchen einen Deal an'],
                  ['recordingEnabled', 'Aufzeichnung und Transkript'],
                  ['addressFromProperty', 'Adresse aus dem Objekt'],
                  ['internalOnly', 'Nur intern buchbar · kein öffentlicher Link'],
                ] as const
              ).map(([key, label]) => (
                <Controller
                  key={key}
                  control={control}
                  name={key}
                  render={({ field }) => (
                    <Toggle
                      label={label}
                      size="large"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={!canEdit || (key === 'recordingEnabled' && staticChannel !== null)}
                      className={clsx(styles.toggle, styles.toggle38, !field.value && styles.toggleOff)}
                    />
                  )}
                />
              ))}
            </div>
            {staticChannel ? (
              <div className={fields.hint}>
                Aufzeichnung und Transkript gibt es nur im DAMOKLES Meetingraum.
              </div>
            ) : null}
            {message('recordingEnabled') ? (
              <div className={styles.fieldError}>{message('recordingEnabled')}</div>
            ) : null}
          </section>
        </div>
      </div>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        eyebrow="Ereignistyp"
        title="Ereignistyp löschen?"
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="destructiveFilled"
              loading={remove.isPending}
              onClick={() => void confirmDelete()}
            >
              Löschen
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>
          „{type?.name}“ wird entfernt. Ereignistypen, die von Terminen verwendet werden, lassen sich nur
          deaktivieren.
        </p>
      </Dialog>
    </form>
  );
}

/** Beratung 06 — editor of one event type (`/calendar/event-types/:id`) or of a new one (`/calendar/event-types/new`). */
export function EventTypeEditorPage() {
  const params = useParams();
  const workspace = useWorkspace();
  const permissions = usePermissions();
  const id = params['id'];
  const type = useEventType(id);
  const mayCreate = canCreateEventTypes(workspace.user.roleKey, permissions.has('settings.organization'));

  if (id === undefined) {
    if (!mayCreate) {
      return (
        <div className={styles.page}>
          <Link to="/calendar/event-types" className={styles.crumb}>
            ← Ereignistypen
          </Link>
          <Banner tone="neutral">Ereignistypen legen Berater und Admins an.</Banner>
        </div>
      );
    }
    return <Editor type={null} />;
  }
  if (type.isPending) return <EditorSkeleton />;
  if (type.isError) {
    return (
      <div className={styles.page}>
        <Link to="/calendar/event-types" className={styles.crumb}>
          ← Ereignistypen
        </Link>
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void type.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(type.error)}
        </Banner>
      </div>
    );
  }
  return <Editor key={type.data.id} type={type.data} />;
}
